
import {
  Component,
  Provide,
  Prop,
  Mixins,
  Watch
} from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IProvider } from "@/models/providers";
import {
  IOrderProvider,
  IOrderProviderMethods
} from "@/models/providers/order";
import { IOrder } from "@/models/orders";
import { IState } from "@/store";
import { ClientRoutes } from "@/data/enums/router";

@Component({
  computed: {
    ...mapState({
      orderScope(state: IState, getters) {
        return getters[`data/${DataModules.ORDERS}/scope`](this.orderId);
      },
      order(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.ORDERS,
          scope: this.orderScope
        });
      },
      isDelegated(state: IState, getters): boolean {
        return getters[`data/${DataModules.ORDERS}/belongsToDelegate`](
          this.order
        );
      }
    })
  }
})
export default class OrderProvider
  extends Mixins(RequestPromises, BillingCyclesStore, Context)
  implements IProvider {
  // Map state

  public order!: IOrder;
  public orderScope!: string;

  // Props
  @Prop({ required: true })
  public orderId!: IOrder["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: true })
  public handleGuarding!: boolean;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({
    default: () => (vm: OrderProvider) => {
      const name = vm.isAdmin ? "adminOrders" : ClientRoutes.ORDERS;
      return vm.$router.push({ name }).catch(err => err);
    }
  })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;
  public isDelegated!: boolean;

  // Computed

  public get referrer() {
    return this.order?.account?.affiliate_referral?.affiliate_account?.account
      ?.client?.[0];
  }

  // Lifecycle methods

  public async created() {
    if (!this.$userCan("staff_get_order")) {
      this.isLoading = false;
      return;
    }

    await this.loadData();
    this.setBreadcrumbKey();
    this.$bus.$on("orderProvider::reloadData", this.reloadData);
  }

  public beforeDestroy() {
    // Remove event listener
    this.$bus.$off("orderProvider::reloadData");
    // Unset meta props
    this.$store.commit("ui/unsetMetaProps", ["orderId"]);
    // Bin order data
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.ORDERS,
      scope: this.orderScope,
      vm: this
    });
  }

  // Watchers

  @Watch("order.number", { immediate: true })
  public onOrderNumberChange(orderNum) {
    this.$store.commit("ui/setMetaProps", {
      orderId: orderNum || ""
    });
  }

  // Methods

  public async loadData({ ignoreStored } = { ignoreStored: false }) {
    try {
      await Promise.all([
        this.getOrderData(ignoreStored),
        this.getBillingCycles()
      ]);
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
      if (this.$store.getters["api/is404"](error) && this.onNotFound)
        return this.onNotFound(this);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    this.loadData({ ignoreStored: true });
  }

  public getOrderData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.ORDERS}/get`, {
      id: this.orderId,
      scope: this.orderScope,
      params: {
        with: [
          "account.affiliate_referral.affiliate_account.account.client",
          "affiliate_commissions",
          "brand",
          "client",
          "client.tags",
          "contract",
          "custom_fields.field",
          "payments",
          "products",
          "products.product.image",
          "products.tags",
          "promotions",
          "status",
          "taxes",
          "taxes.tax_tag_data"
        ].join(",")
      },
      vm: this,
      ignoreStored
    });
  }

  public setBreadcrumbKey() {
    this.$store.commit("ui/breadcrumb/add", {
      key: "order-id",
      value: this.orderId
    });
  }

  // Provide

  @Provide("orderProvider")
  public providerData(): IOrderProvider {
    return {
      billingCycles: this.billingCycles,
      hasRequestError: this.hasRequestError,
      isDelegated: this.isDelegated,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      order: this.order,
      referrer: this.referrer
    };
  }

  @Provide("orderProviderMethods")
  public get providerMethods(): IOrderProviderMethods {
    return {
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.$userCan("staff_get_order"))
      return h("guard", { props: { ifAdmin: false, ifClient: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $orderData: this.providerData(),
        $orderMethods: this.providerMethods
      })
    );
  }
}
